"use client";

import React from "react";
import {
  alpha,
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import BestPairedDesktop from "./BestPairedDesktop";
import CloseIcon from "@mui/icons-material/Close";

/** Viewport &lt; 600px — same as MUI `theme.breakpoints.down("sm")`, works without ThemeProvider (dialog portals to body). */
function useIsMobileView() {
  return useMediaQuery("(max-width:599.95px)", {
    defaultMatches: false,
    noSsr: true,
  });
}

interface BestPairedDesktopWraperProps {
  openBestPaired: any;
  setOpenBestPaired: any;
  recommendedServices: any;
  handleViewDetails: any;
  updateCard: any;
  setUpdateCard: any;
  reloadViewDetailsItem: any;
  handleOpenViewDetailsRsOne: any;
}

export const BestPairedDesktopWraper: React.FC<
  BestPairedDesktopWraperProps
> = ({
  openBestPaired,
  setOpenBestPaired,
  recommendedServices,
  handleViewDetails,
  updateCard,
  setUpdateCard,
  reloadViewDetailsItem,
  handleOpenViewDetailsRsOne,
}) => {
  const isMobileView = useIsMobileView();

  return (
    <Dialog
      open={openBestPaired}
      onClose={() => setOpenBestPaired(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth
      fullScreen={isMobileView}
      sx={{
        maxWidth: isMobileView ? "100%" : "800px",
        margin: "auto",
        "& .MuiDialog-paper": {
          overflow: "hidden",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: isMobileView ? 0 : "20px",
          overflow: "hidden",
          border: isMobileView
            ? "none"
            : `1px solid ${alpha("#1f7a4d", 0.12)}`,
          boxShadow: isMobileView
            ? "none"
            : "0 24px 48px rgba(15, 23, 17, 0.12)",
          maxHeight: isMobileView ? "100%" : "min(90vh, 900px)",
          display: "flex",
          flexDirection: "column",
          m: isMobileView ? 0 : undefined,
        },
      }}
    >
      <DialogContent
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
          p: isMobileView ? 1.5 : 1,
          pt: isMobileView ? "calc(12px + env(safe-area-inset-top, 0px))" : 1,
          pb: isMobileView ? "calc(12px + env(safe-area-inset-bottom, 0px))" : 1,
          background:
            "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,252,251,1) 100%)",
        }}
      >
        <Box sx={{ width: "100%", textAlign: "end", flexShrink: 0 }}>
          <IconButton
            onClick={() => setOpenBestPaired(false)}
            aria-label="Close"
            sx={{
              color: "#1f7a4d",
              bgcolor: alpha("#1f7a4d", 0.08),
              "&:hover": { bgcolor: alpha("#1f7a4d", 0.15) },
            }}
          >
            <CloseIcon sx={{ cursor: "pointer" }} />
          </IconButton>
        </Box>

        {/* Mobile viewport only: horizontal strip; desktop never mounts this branch */}
        {isMobileView ? (
          <Box
            className="salontym-best-paired-hscroll"
            onWheel={(e) => {
              const el = e.currentTarget;
              if (el.scrollWidth > el.clientWidth) {
                e.stopPropagation();
              }
            }}
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              alignItems: "stretch",
              gap: 0,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              flex: "1 1 auto",
              minHeight: 0,
              overflowX: "auto",
              overflowY: "hidden",
              pb: 1,
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              overscrollBehaviorX: "contain",
              overscrollBehaviorY: "none",
              touchAction: "pan-x",
              scrollbarGutter: "stable",
              isolation: "isolate",
              position: "relative",
              zIndex: 0,
              "&::-webkit-scrollbar": {
                height: 8,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha("#1f7a4d", 0.35),
                borderRadius: 4,
              },
            }}
          >
            {recommendedServices?.map((item: any, index: any) => (
              <Box
                key={`m-${index}`}
                sx={{
                  flex: "0 0 auto",
                  width: 244,
                  minWidth: 244,
                  maxWidth: 244,
                  boxSizing: "border-box",
                }}
              >
                <BestPairedDesktop
                  addOn={item?.addOn}
                  id={item?.id}
                  cid={item?.cid}
                  url={
                    item?.banner?.secureHttpPath ??
                    item?.coverImage.secureHttpPath
                  }
                  heading={item?.name}
                  subHeading={item?.subHeading}
                  price={item?.mrp}
                  finalPrice={item?.amount}
                  aboutList={item?.aboutList}
                  serviceCharge={item?.serviceCharge}
                  productCost={item?.productCost}
                  min={item?.min}
                  specialPrice={item?.specialPrice}
                  specialMessage={item?.specialMessage}
                  handleViewDetailsBestPaired={() => handleViewDetails(item)}
                  updateCard={updateCard}
                  setUpdateCard={() => setUpdateCard(false)}
                  reloadViewDetailsItem={reloadViewDetailsItem}
                  productDetailsAvailable={item?.productDetailsAvailable}
                  handleOpenViewDetailsRsOne={() =>
                    handleOpenViewDetailsRsOne(item, index)
                  }
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Grid
            container
            spacing={2}
            sx={{
              width: "100%",
              flex: "1 1 auto",
              minHeight: 0,
              maxHeight: "min(72vh, 720px)",
              overflowY: "auto",
              overflowX: "hidden",
              overscrollBehavior: "contain",
              alignContent: "flex-start",
            }}
          >
            {recommendedServices?.map((item: any, index: any) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={4}
                key={index}
                justifyItems="center"
              >
                <BestPairedDesktop
                  addOn={item?.addOn}
                  id={item?.id}
                  cid={item?.cid}
                  url={
                    item?.banner?.secureHttpPath ??
                    item?.coverImage.secureHttpPath
                  }
                  heading={item?.name}
                  subHeading={item?.subHeading}
                  price={item?.mrp}
                  finalPrice={item?.amount}
                  aboutList={item?.aboutList}
                  serviceCharge={item?.serviceCharge}
                  productCost={item?.productCost}
                  min={item?.min}
                  specialPrice={item?.specialPrice}
                  specialMessage={item?.specialMessage}
                  handleViewDetailsBestPaired={() => handleViewDetails(item)}
                  updateCard={updateCard}
                  setUpdateCard={() => setUpdateCard(false)}
                  reloadViewDetailsItem={reloadViewDetailsItem}
                  productDetailsAvailable={item?.productDetailsAvailable}
                  handleOpenViewDetailsRsOne={() =>
                    handleOpenViewDetailsRsOne(item, index)
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};
